import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Modal,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { modalStyles } from 'src/pages/dashboard/UsersDetailsLookup';
import {
  accessExpiryDate,
  assessmentAccessTypeLabel,
  PRO_FEATURES,
  ProAccessType,
  ProfessionalAccessData,
  ProFeature
} from 'src/models/professionalAccess';

// Grants / replaces a user's professional feature access. Behaves as a
// single-active upsert on the admin graph (see Admin Service API docs).
const PROFESSIONAL_ACCESS_GRANT = gql`
  mutation ProfessionalAccessGrant($input: ProfessionalAccessGrantInput!) {
    ProfessionalAccessGrant(input: $input) {
      id
      features
      assessmentAccessType
      totalAssessments
      availableAssessments
      headsetLimit
      grantsLinkedUserMembershipTrialDays
      expiryDate {
        formatted
      }
    }
  }
`;

type Props = {
  open: boolean;
  userId: string;
  hasActiveAccess: boolean;
  // The current active access, when editing. Pre-fills the form so admins
  // change what is already saved instead of starting from scratch.
  existingAccess?: ProfessionalAccessData;
  onClose: () => void;
  onGranted: () => void;
};

const GrantProfessionalAccessModal = ({
  open,
  userId,
  hasActiveAccess,
  existingAccess,
  onClose,
  onGranted
}: Props) => {
  const isEditing = !!existingAccess;
  const initialExpiry = existingAccess
    ? accessExpiryDate(existingAccess)
    : undefined;

  const [features, setFeatures] = useState<ProFeature[]>(
    existingAccess?.features?.length
      ? existingAccess.features
      : ['PRO_ASSESSMENTS']
  );
  const [assessmentAccessType, setAssessmentAccessType] =
    useState<ProAccessType>(
      existingAccess?.assessmentAccessType ?? 'UNLIMITED'
    );
  const [totalAssessments, setTotalAssessments] = useState<string>(
    existingAccess?.totalAssessments != null
      ? String(existingAccess.totalAssessments)
      : ''
  );
  const [headsetLimit, setHeadsetLimit] = useState<string>(
    existingAccess?.headsetLimit ? String(existingAccess.headsetLimit) : '1'
  );
  const [expiryDate, setExpiryDate] = useState<string>(
    initialExpiry ? format(initialExpiry, 'yyyy-MM-dd') : ''
  );
  const [
    grantsLinkedUserMembershipTrialDays,
    setGrantsLinkedUserMembershipTrialDays
  ] = useState<string>(
    existingAccess?.grantsLinkedUserMembershipTrialDays != null
      ? String(existingAccess.grantsLinkedUserMembershipTrialDays)
      : ''
  );
  const [resetLimits, setResetLimits] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  const [executeGrant, { loading, error }] = useMutation(
    PROFESSIONAL_ACCESS_GRANT,
    { errorPolicy: 'all' }
  );

  const includesAssessments = features.includes('PRO_ASSESSMENTS');
  const isFixed = includesAssessments && assessmentAccessType === 'FIXED';

  const toggleFeature = (feature: ProFeature) => {
    setFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature]
    );
  };

  const validate = (): string | undefined => {
    if (features.length === 0) {
      return 'Select at least one feature.';
    }
    if (includesAssessments && !assessmentAccessType) {
      return 'Select an assessment access type.';
    }
    if (isFixed && (!totalAssessments || Number(totalAssessments) < 1)) {
      return 'Fixed access requires a total assessment count of at least 1.';
    }
    // The headset limit field only applies to unlimited access; it must be a
    // positive value (zero is not allowed).
    if (!isFixed && (headsetLimit === '' || Number(headsetLimit) < 1)) {
      return 'Headset limit must be at least 1.';
    }
    if (
      grantsLinkedUserMembershipTrialDays !== '' &&
      Number(grantsLinkedUserMembershipTrialDays) < 0
    ) {
      return 'Client trial membership days cannot be negative.';
    }
    return undefined;
  };

  const handleSubmit = async () => {
    const message = validate();
    setValidationError(message);
    if (message) return;

    const input: Record<string, unknown> = {
      userId,
      features,
      // Reset limits is only offered when editing a fixed-count grant; never
      // send a stale `true` for other access types.
      resetLimits: isEditing && isFixed && resetLimits
    };

    if (includesAssessments) {
      input.assessmentAccessType = assessmentAccessType;
      if (isFixed) {
        input.totalAssessments = Number(totalAssessments);
      }
    }

    // Headset limit is only meaningful for unlimited access; for fixed-count
    // access it is omitted from the request entirely (batches inherit the
    // headset limit from the Pro membership).
    if (!isFixed) {
      input.headsetLimit = headsetLimit === '' ? 0 : Number(headsetLimit);
    }

    if (expiryDate) {
      // Input expects a DateTime object with a `formatted` ISO string.
      input.expiryDate = { formatted: new Date(expiryDate).toISOString() };
    }

    // Days of free trial membership granted to a linked client when they are
    // first connected to this professional access. Omitted when left blank.
    if (grantsLinkedUserMembershipTrialDays !== '') {
      input.grantsLinkedUserMembershipTrialDays = Number(
        grantsLinkedUserMembershipTrialDays
      );
    }

    const result = await executeGrant({ variables: { input } });

    if (result.data?.ProfessionalAccessGrant) {
      // Re-fetch the user so the active access list reflects the new grant.
      onGranted();
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={modalStyles}>
      <Card
        sx={{ maxWidth: 560, mx: 'auto', maxHeight: '100%', overflow: 'auto' }}
      >
        <CardContent>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {isEditing
              ? 'Update Professional Feature Access'
              : 'Grant Professional Feature Access'}
          </Typography>
          {hasActiveAccess && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              This user already has active professional access. Granting will
              replace the current active access.
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Features
              </Typography>
              <FormGroup>
                {PRO_FEATURES.map((feature) => (
                  <FormControlLabel
                    key={feature.value}
                    control={
                      <Checkbox
                        checked={features.includes(feature.value)}
                        onChange={() => toggleFeature(feature.value)}
                      />
                    }
                    label={feature.label}
                  />
                ))}
              </FormGroup>
            </Box>

            {includesAssessments && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Assessment access type
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  SelectProps={{ native: true }}
                  value={assessmentAccessType}
                  onChange={(e) =>
                    setAssessmentAccessType(e.target.value as ProAccessType)
                  }
                >
                  <option value="UNLIMITED">
                    {assessmentAccessTypeLabel('UNLIMITED')}
                  </option>
                  <option value="FIXED">
                    {assessmentAccessTypeLabel('FIXED')}
                  </option>
                </TextField>
              </Box>
            )}

            {isFixed && (
              <TextField
                label="Total assessments"
                type="number"
                size="small"
                fullWidth
                inputProps={{ min: 1 }}
                value={totalAssessments}
                onChange={(e) => setTotalAssessments(e.target.value)}
              />
            )}

            {!isFixed && (
              <TextField
                label="Headset limit"
                type="number"
                size="small"
                fullWidth
                inputProps={{ min: 1 }}
                value={headsetLimit}
                onChange={(e) => setHeadsetLimit(e.target.value)}
              />
            )}

            <TextField
              label="Expiry date (optional)"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for no expiry."
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />

            <TextField
              label="Client Trial Membership Days"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Days of free trial membership granted to linked clients. Leave blank to keep none."
              value={grantsLinkedUserMembershipTrialDays}
              onChange={(e) =>
                setGrantsLinkedUserMembershipTrialDays(e.target.value)
              }
            />

            {isEditing && isFixed && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resetLimits}
                    onChange={(e) => setResetLimits(e.target.checked)}
                  />
                }
                label={`Clear existing usage (${existingAccess?.usedAssessments ?? 0} assessments used)`}
              />
            )}

            {(validationError || error) && (
              <FormHelperText error>
                {validationError ?? error?.message}
              </FormHelperText>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading
                  ? isEditing
                    ? 'Updating…'
                    : 'Granting…'
                  : isEditing
                    ? 'Update access'
                    : 'Grant access'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default GrantProfessionalAccessModal;
